import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  closestCorners,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragOverEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";

import { WatchlistService } from "../services/watchlist-service";
import type { Watchlist, WatchlistItem } from "@/schemas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Loader2, Plus, Bookmark, Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useCurrencyMap } from "@/hooks/use-currency-map";
import { WatchlistCard } from "./WatchlistCard";
import { WatchlistAssetItemOverlay } from "./WatchlistAssetItem";
import { RenameWatchlistDialog } from "./RenameWatchlistDialog";
import { AssetDetailModal } from "./AssetDetailModal";
import { EditItemDialog } from "./EditItemDialog";
import { AddAssetToWatchlistModal } from "./AddAssetToWatchlistModal";

type ItemsMap = Record<string, WatchlistItem[]>;

function buildReverseMap(itemsMap: ItemsMap): Map<string, string> {
  const map = new Map<string, string>();
  for (const [watchlistId, items] of Object.entries(itemsMap)) {
    for (const item of items) {
      map.set(item.item_id, watchlistId);
    }
  }
  return map;
}

export default function WatchlistsPage() {
  const [watchlists, setWatchlists] = useState<Watchlist[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [itemsMap, setItemsMap] = useState<ItemsMap>({});

  // Create dialog state
  const [creating, setCreating] = useState(false);
  const [newWatchlistName, setNewWatchlistName] = useState("");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  // Add asset modal state
  const [isAddAssetModalOpen, setIsAddAssetModalOpen] = useState(false);

  // Rename dialog state
  const [renameWatchlist, setRenameWatchlist] = useState<Watchlist | null>(
    null
  );

  // Asset detail modal state
  const [selectedItem, setSelectedItem] = useState<WatchlistItem | null>(null);

  // Edit item dialog state
  const [editItem, setEditItem] = useState<WatchlistItem | null>(null);
  const [editWatchlistId, setEditWatchlistId] = useState<string | null>(null);

  // DnD state
  const [activeItem, setActiveItem] = useState<WatchlistItem | null>(null);
  const [dragOverWatchlistId, setDragOverWatchlistId] = useState<string | null>(null);
  const preDragItemsMap = useRef<ItemsMap | null>(null);
  // O(1) item→watchlist lookup, kept in sync via ref to avoid re-render loops
  const reverseMapRef = useRef<Map<string, string>>(new Map());

  const { resolveCurrency } = useCurrencyMap();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 6 },
    })
  );

  // Keep reverseMap in sync whenever itemsMap changes
  useEffect(() => {
    reverseMapRef.current = buildReverseMap(itemsMap);
  }, [itemsMap]);

  const loadWatchlistsData = useCallback(async () => {
    try {
      setLoadingList(true);
      const data = await WatchlistService.listWithItems();
      const nextItemsMap: ItemsMap = {};
      data.forEach(({ watchlist_id, items }) => {
        nextItemsMap[watchlist_id] = items;
      });
      setWatchlists(data);
      setItemsMap(nextItemsMap);
      return data;
    } catch (error) {
      console.error(error);
      toast.error("Unable to load watchlists");
      return [];
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    loadWatchlistsData();
  }, [loadWatchlistsData]);

  const refreshAll = useCallback(async () => {
    await loadWatchlistsData();
  }, [loadWatchlistsData]);

  // Build a flat map of itemId → WatchlistItem for drag overlay
  const allItemsById = useMemo(() => {
    const map = new Map<string, WatchlistItem>();
    for (const items of Object.values(itemsMap)) {
      for (const item of items) {
        map.set(item.item_id, item);
      }
    }
    return map;
  }, [itemsMap]);

  // ─── DnD Handlers ──────────────────────────────────────────────

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const item = allItemsById.get(String(event.active.id));
      setActiveItem(item ?? null);
      preDragItemsMap.current = structuredClone(itemsMap);
    },
    [allItemsById, itemsMap]
  );

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { over } = event;
    if (!over) {
      setDragOverWatchlistId((prev) => (prev === null ? prev : null));
      return;
    }

    const overId = String(over.id);
    const nextWatchlistId = overId.startsWith("watchlist-")
      ? overId.replace("watchlist-", "")
      : reverseMapRef.current.get(overId) ?? null;

    setDragOverWatchlistId((prev) =>
      prev === nextWatchlistId ? prev : nextWatchlistId
    );
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveItem(null);
      setDragOverWatchlistId(null);

      if (!over || !preDragItemsMap.current) {
        if (preDragItemsMap.current) {
          setItemsMap(preDragItemsMap.current);
        }
        preDragItemsMap.current = null;
        return;
      }

      const activeId = String(active.id);
      const overId = String(over.id);
      const savedState = preDragItemsMap.current;
      preDragItemsMap.current = null;

      const sourceWatchlistId = reverseMapRef.current.get(activeId) ?? null;
      const destWatchlistId = overId.startsWith("watchlist-")
        ? overId.replace("watchlist-", "")
        : reverseMapRef.current.get(overId) ?? null;

      if (!sourceWatchlistId || !destWatchlistId) {
        setItemsMap(savedState);
        return;
      }

      if (sourceWatchlistId !== destWatchlistId) {
        const sourceItems = [...(itemsMap[sourceWatchlistId] || [])];
        const destItems = [...(itemsMap[destWatchlistId] || [])];
        const sourceIndex = sourceItems.findIndex((i) => i.item_id === activeId);

        if (sourceIndex === -1) {
          setItemsMap(savedState);
          return;
        }

        const [movedItem] = sourceItems.splice(sourceIndex, 1);
        const overIndex = overId.startsWith("watchlist-")
          ? destItems.length
          : destItems.findIndex((i) => i.item_id === overId);
        const targetIndex = overIndex >= 0 ? overIndex : destItems.length;

        try {
          const createdItem = await WatchlistService.moveItem(
            sourceWatchlistId,
            activeId,
            destWatchlistId
          );

          const nextDestItems = [...destItems];
          nextDestItems.splice(targetIndex, 0, {
            ...createdItem,
            asset: movedItem.asset ?? createdItem.asset,
            latest_price: movedItem.latest_price ?? createdItem.latest_price,
            latest_price_currency:
              movedItem.latest_price_currency ?? createdItem.latest_price_currency,
            daily_change_percent:
              movedItem.daily_change_percent ?? createdItem.daily_change_percent,
          });

          setItemsMap((prev) => ({
            ...prev,
            [sourceWatchlistId]: sourceItems,
            [destWatchlistId]: nextDestItems,
          }));

          reverseMapRef.current.delete(activeId);
          reverseMapRef.current.set(createdItem.item_id, destWatchlistId);

          await Promise.all([
            WatchlistService.reorderItems(
              sourceWatchlistId,
              sourceItems.map((item) => item.item_id)
            ),
            WatchlistService.reorderItems(
              destWatchlistId,
              nextDestItems.map((item) => item.item_id)
            ),
          ]);
        } catch (error) {
          console.error(error);
          setItemsMap(savedState);
          toast.error(
            error instanceof Error ? error.message : "Failed to move asset"
          );
        }
      } else {
        const items = itemsMap[sourceWatchlistId];
        if (!items) return;

        const activeIndex = items.findIndex((i) => i.item_id === activeId);
        const overIndex = overId.startsWith("watchlist-")
          ? items.length - 1
          : items.findIndex((i) => i.item_id === overId);

        if (
          activeIndex === -1 ||
          overIndex === -1 ||
          activeIndex === overIndex
        ) {
          return;
        }

        const reordered = arrayMove(items, activeIndex, overIndex);
        setItemsMap((prev) => ({
          ...prev,
          [sourceWatchlistId]: reordered,
        }));

        try {
          await WatchlistService.reorderItems(
            sourceWatchlistId,
            reordered.map((i) => i.item_id)
          );
        } catch (error) {
          console.error(error);
          setItemsMap(savedState);
          toast.error("Failed to reorder items");
        }
      }
    },
    [itemsMap]
  );

  const handleDragCancel = useCallback(() => {
    setActiveItem(null);
    setDragOverWatchlistId(null);
    if (preDragItemsMap.current) {
      setItemsMap(preDragItemsMap.current);
      preDragItemsMap.current = null;
    }
  }, []);

  // ─── CRUD Handlers ──────────────────────────────────────────────

  const handleCreateWatchlist = async () => {
    const name = newWatchlistName.trim();
    if (!name) return;

    try {
      setCreating(true);
      const created = await WatchlistService.create(name);
      setWatchlists((prev) => [...prev, created]);
      setItemsMap((prev) => ({ ...prev, [created.watchlist_id]: [] }));
      setNewWatchlistName("");
      setIsCreateDialogOpen(false);
      toast.success("Watchlist created");
    } catch (error) {
      console.error(error);
      toast.error("Failed to create watchlist");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteWatchlist = async (watchlist: Watchlist) => {
    try {
      await WatchlistService.remove(watchlist.watchlist_id);
      setWatchlists((prev) =>
        prev.filter((wl) => wl.watchlist_id !== watchlist.watchlist_id)
      );
      setItemsMap((prev) => {
        const newMap = { ...prev };
        delete newMap[watchlist.watchlist_id];
        return newMap;
      });
      toast.success("Watchlist deleted");
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete watchlist");
    }
  };

  const handleRequestRename = useCallback((watchlist: Watchlist) => {
    setRenameWatchlist(watchlist);
  }, []);

  const handleRequestAssetClick = useCallback((item: WatchlistItem) => {
    setSelectedItem(item);
  }, []);

  const handleRequestEditItem = useCallback(
    (watchlistId: string, item: WatchlistItem) => {
      setEditItem(item);
      setEditWatchlistId(watchlistId);
    },
    []
  );

  const handleRenameComplete = (updated: Watchlist) => {
    setWatchlists((prev) =>
      prev.map((wl) =>
        wl.watchlist_id === updated.watchlist_id ? updated : wl
      )
    );
  };

  const handleWatchlistCreatedFromModal = (created: Watchlist) => {
    setWatchlists((prev) => [...prev, created]);
    setItemsMap((prev) => ({ ...prev, [created.watchlist_id]: [] }));
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Actions */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        {!loadingList && watchlists.length > 0 && (
          <Button
            size="sm"
            className="gap-2"
            onClick={() => setIsAddAssetModalOpen(true)}
          >
            <Search className="h-4 w-4" />
            Add Asset
          </Button>
        )}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline" className="gap-2">
              <Plus className="h-4 w-4" />
              New Watchlist
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Watchlist</DialogTitle>
              <DialogDescription>
                Enter a name for your new watchlist to start tracking assets.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="name" className="mb-2 block">
                Name
              </Label>
              <Input
                id="name"
                placeholder="e.g. High Growth Tech"
                value={newWatchlistName}
                onChange={(e) => setNewWatchlistName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCreateWatchlist();
                }}
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setIsCreateDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateWatchlist}
                disabled={creating || !newWatchlistName.trim()}
              >
                {creating && (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                )}
                Create Watchlist
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Content */}
      {loadingList ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : watchlists.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-muted/30 flex items-center justify-center mb-4">
            <Bookmark className="h-8 w-8 text-muted-foreground/50" />
          </div>
          <h3 className="text-lg font-semibold mb-2">No watchlists yet</h3>
          <p className="text-sm text-muted-foreground mb-4 max-w-sm">
            Create your first watchlist to start tracking assets, set target
            prices, and monitor your favorite stocks, ETFs, and crypto.
          </p>
          <Button
            onClick={() => setIsCreateDialogOpen(true)}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Create Your First Watchlist
          </Button>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {watchlists.map((watchlist) => (
              <WatchlistCard
                key={watchlist.watchlist_id}
                watchlist={watchlist}
                items={itemsMap[watchlist.watchlist_id] || []}
                allWatchlists={watchlists}
                resolveCurrency={resolveCurrency}
                onRename={handleRequestRename}
                onDelete={handleDeleteWatchlist}
                onAssetClick={handleRequestAssetClick}
                onItemsChange={refreshAll}
                onEditItem={handleRequestEditItem}
                isDragOver={
                  activeItem !== null &&
                  dragOverWatchlistId === watchlist.watchlist_id
                }
              />
            ))}
          </div>

          <DragOverlay dropAnimation={null}>
            {activeItem && (
              <WatchlistAssetItemOverlay
                item={activeItem}
                resolveCurrency={resolveCurrency}
              />
            )}
          </DragOverlay>
        </DndContext>
      )}

      {/* Add Asset Modal */}
      <AddAssetToWatchlistModal
        open={isAddAssetModalOpen}
        onOpenChange={setIsAddAssetModalOpen}
        watchlists={watchlists}
        onItemAdded={refreshAll}
        onWatchlistCreated={handleWatchlistCreatedFromModal}
      />

      {/* Rename Dialog */}
      <RenameWatchlistDialog
        watchlist={renameWatchlist}
        isOpen={renameWatchlist !== null}
        onClose={() => setRenameWatchlist(null)}
        onRenamed={handleRenameComplete}
      />

      {/* Asset Detail Modal */}
      <AssetDetailModal
        item={selectedItem}
        isOpen={selectedItem !== null}
        onClose={() => setSelectedItem(null)}
      />

      {/* Edit Item Dialog */}
      <EditItemDialog
        item={editItem}
        watchlistId={editWatchlistId}
        isOpen={editItem !== null}
        onClose={() => {
          setEditItem(null);
          setEditWatchlistId(null);
        }}
        onUpdated={refreshAll}
      />

      <Toaster />
    </div>
  );
}
